import {z} from 'zod';

import {LeadQualityEnumSchema} from '@dms/api/sales';
import {
  FullVehicleResponseBody,
  UserResponseBody,
  MoneySchema,
  UuidResponseBody,
} from '@dms/api/shared';

export const CreateVehiclePriceReportRequestBodySchema = z.object({
  country: z.string().nullable().optional(),
});

export const CreatePriceReportResponseBodySchema = z.object({
  id: z.string(),
  url: z.string(),
  country: z.string(),
  createdAt: z.string(),
});

export const PriceReportResponseBodySchema = z.object({
  id: z.string(),
  requestId: z.string(),
  url: z.string(),
  country: z.string(),
  createdAt: z.string(),
  createdBy: z.string().nullable(),
  sellingPrice: z.number().nullable().optional(),
  sellingPriceCurrency: z.string().nullable().optional(),
  sellingPriceMin: z.number().nullable().optional(),
  sellingPriceMinCurrency: z.string().nullable().optional(),
  sellingPriceMax: z.number().nullable().optional(),
  sellingPriceMaxCurrency: z.string().nullable().optional(),
  purchasePrice: z.number().nullable().optional(),
  purchasePriceCurrency: z.string().nullable().optional(),
  turnover: z.number().nullable().optional(),
  featuresLevel: z.number().nullable().optional(),
});

export const VehicleInfoSchema = z.object({
  id: z.string(),
  make: z.string().nullable(),
  trim: z.string().nullable(),
  modelFamily: z.string().nullable(),
  variant: z.string().nullable(),
  title: z.string().nullable(),
});

export const InterestActiveBusinessCaseResponseBodySchema = z.object({
  businessCaseId: z.string(),
  code: z.string(),
  ownerId: z.string(),
  ownerFirstName: z.string(),
  ownerLastName: z.string(),
  businessCaseType: z.string().nullable(),
  businessCaseState: z.string(),
  vehicleInfo: VehicleInfoSchema,
  createdAt: z.string(),
  sourceCodeId: z.string().nullable(),
});

export const UtmParametersSchema = z.object({
  source: z.string().nullable(),
  medium: z.string().nullable(),
  campaign: z.string().nullable(),
  content: z.string().nullable(),
  term: z.string().nullable(),
});

export const InterestStateSchema = z.enum(['OPEN', 'NEW', 'CLOSED', 'UNSUCCESSFUL']);
export const InterestTypeSchema = z.enum(['SELLING', 'BUYING', 'SWAP']);

export const UserResponseBodySchema = z.custom<UserResponseBody>();

export const InterestResponseBodySchema = z.object({
  id: z.string().nullable().optional(),
  customerId: z.string().nullable().optional(),
  createdBy: z.string().nullable().optional(),
  createdAt: z.string().nullable().optional(),
  updatedBy: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
  sourceCodeId: z.string().nullable().optional(),
  utm: UtmParametersSchema.nullable().optional(),
  type: InterestTypeSchema.nullable().optional(),
  state: InterestStateSchema.nullable().optional(),
  reasonCloseCodeId: z.string().nullable().optional(),
  reasonCloseNote: z.string().nullable().optional(),
  minisalesIds: z.array(z.string()).optional(),
  minipurchasesIds: z.array(z.string()).optional(),
  closedBy: UserResponseBodySchema.nullable().optional(),
  closedAt: z.string().nullable().optional(),
  publicId: z.string().nullable().optional(),
  activeBusinessCases: z.array(InterestActiveBusinessCaseResponseBodySchema).optional(),
  participationId: z.string().nullable().optional(),
  firstResponseAt: z.string().nullable().optional(),
  leadQuality: LeadQualityEnumSchema.nullable(),
  branchId: z.string().nullable().optional(),
});

export const CreateInterestRequestBodySchema = z.object({
  customerId: z.string().nullable(),
  sourceCodeId: z.string(),
  minipurchaseVehicleId: z.string().nullable().optional(),
});

export const UpdateInterestRequestBodySchema = z.object({
  customerId: z.string().nullable().optional(),
  sourceCodeId: z.string().nullable().optional(),
  state: InterestStateSchema.nullable().optional(),
  reasonCloseNote: z.string().nullable().optional(),
  reasonCloseCodeId: z.string().nullable().optional(),
  type: InterestTypeSchema.nullable().optional(),
  branchId: z.string().nullable().optional(),
});

export const TranslationRowResponseBodySchema = z.object({
  key: z.string(),
  name: z.string(),
});

export const InterestCatalogueResponseBodySchema = z.object({
  source: z.array(TranslationRowResponseBodySchema),
  type: z.array(TranslationRowResponseBodySchema),
  interestRate: z.array(TranslationRowResponseBodySchema),
  state: z.array(z.string()),
  reasonClose: z.array(TranslationRowResponseBodySchema),
});

export const InterestStatisticsResponseBodySchema = z.object({
  open: z.number(),
  sell: z.number(),
  buy: z.number(),
  swap: z.number(),
  openInWeek: z.number(),
  sellInWeek: z.number(),
  buyInWeek: z.number(),
  swapInWeek: z.number(),
});

export const GetOfferedVehiclesListRequestBodySchema = z.object({
  vehicleIds: z.array(z.string()),
});

export const ChangeInterestBranchRequestBodySchema = z.object({
  branchId: z.string(),
});

export const ChangeInterestCustomerRequestBodySchema = z.object({
  customerId: z.string(),
});

export const ChangeInterestBranchApiArgSchema = z.object({
  interestId: z.string(),
  changeInterestBranchRequestBody: ChangeInterestBranchRequestBodySchema,
});

export const ChangeInterestCustomerApiArgSchema = z.object({
  interestId: z.string(),
  changeInterestCustomerRequestBody: ChangeInterestCustomerRequestBodySchema,
});

export const MinipurchaseFilteredSpecificationsSchema = z.object({
  make: z.array(z.string()).nullable().optional(),
  modelFamily: z.array(z.string()).nullable().optional(),
  trimLevel: z.string().nullable().optional(),
  transmission: z.array(z.string()).nullable().optional(),
  bodyStyle: z.array(z.string()).nullable().optional(),
  bodyColor: z.array(z.string()).nullable().optional(),
  labelIds: z.array(z.string()).nullable().optional(),
  salePresetId: z.string().nullable().optional(),
  drive: z.array(z.string()).nullable().optional(),
  features: z.array(z.string()).nullable().optional(),
  fuelType: z.array(z.string()).nullable().optional(),
  mileage: z.array(z.number().nullable()).nullable().optional(),
  power: z.array(z.number().nullable()).nullable().optional(),
  year: z.array(z.number().nullable()).nullable().optional(),
  price: z.array(z.number().nullable()).nullable().optional(),
  cubicCapacity: z.array(z.number().nullable()).nullable().optional(),
  type: z.string().nullable().optional(),
  vatDeductible: z.boolean().nullable().optional(),
  condition: z.array(z.string()).nullable().optional(),
});

export const MinipurchaseResponseBodySchema = z.object({
  id: z.string(),
  interestId: z.string(),
  reason: z.string().nullable().optional(),
  costReason: z.string().nullable().optional(),
  interestRate: z.number().nullable().optional(),
  filteredSpecifications: MinipurchaseFilteredSpecificationsSchema.nullable().optional(),
  offeredVehiclesIds: z.array(z.string()).nullable().optional(),
  additionalInformation: z.string().nullable().optional(),
  testDriveRequested: z.boolean().nullable().optional(),
  testDriveScheduledAt: z.string().nullable().optional(),
  testDriveNote: z.string().nullable().optional(),
  preferredDownPayment: MoneySchema.nullable().optional(),
  paymentPreferenceCodeId: z.string().nullable().optional(),
});

export const CreateMinipurchaseRequestBodySchema = z.object({
  interestId: z.string(),
  reason: z.string().nullable().optional(),
  costReason: z.string().nullable().optional(),
  interestRate: z.number().nullable().optional(),
  filteredSpecifications: z.object({}).nullable().optional(),
  offeredVehiclesIds: z.any().nullable().optional(),
  additionalInformation: z.string().nullable().optional(),
  testDriveRequested: z.boolean().nullable().optional(),
  testDriveScheduledAt: z.string().nullable().optional(),
  testDriveNote: z.string().nullable().optional(),
  preferredDownPayment: MoneySchema.nullable().optional(),
  paymentPreferenceCodeId: z.string().nullable().optional(),
});

export const UpdateMinipurchaseRequestBodySchema = z.object({
  minipurchaseId: z.string(),
  reason: z.string().nullable().optional(),
  costReason: z.string().nullable().optional(),
  interestRate: z.number().nullable().optional(),
  filteredSpecifications: z.object({}).nullable().optional(),
  offeredVehiclesIds: z.any().nullable().optional(),
  additionalInformation: z.string().nullable().optional(),
  testDriveRequested: z.boolean().nullable().optional(),
  testDriveScheduledAt: z.string().nullable().optional(),
  testDriveNote: z.string().nullable().optional(),
  preferredDownPayment: MoneySchema.nullable().optional(),
  paymentPreferenceCodeId: z.string().nullable().optional(),
});

export const InterestVehicleTypeSchema = z.enum([
  'VEHICLETYPE_AGRICULTURAL_MACHINE',
  'VEHICLETYPE_CONSTRUCTION_MACHINE',
  'VEHICLETYPE_PASSENGER_CAR',
  'VEHICLETYPE_VAN',
  'VEHICLETYPE_MOTORCYCLE',
  'VEHICLETYPE_TRUCK',
  'VEHICLETYPE_SEMI_TRAILER',
  'VEHICLETYPE_TRAILER',
  'VEHICLETYPE_BUS',
  'VEHICLETYPE_OTHER',
  'VEHICLETYPE_CARAVAN',
]);

export const InterestVehicleDataSchema = z.object({
  type: z.string().nullable().optional(),
  vin: z.string().nullable().optional(),
  make: z.string().nullable().optional(),
  note: z.string().nullable().optional(),
  modelFamily: z.string().nullable().optional(),
  trim: z.string().nullable().optional(),
  variant: z.string().nullable().optional(),
  fuelType: z.string().nullable().optional(),
  transmission: z.string().nullable().optional(),
  bodyStyle: z.string().nullable().optional(),
  drive: z.string().nullable().optional(),
  firstRegistration: z.string().nullable().optional(),
  realProductionYear: z.string().nullable().optional(),
  mileage: z.number().nullable().optional(),
  secondaryFuelType: z.string().nullable().optional(),
  licencePlate: z.string().nullable().optional(),
  features: z.any().nullable().optional(),
  power: z.number().nullable().optional(),
  engineVolume: z.number().nullable().optional(),
  maximalPower: z.number().nullable().optional(),
});

export const VatTypeSchema = z.enum(['S', 'R', 'MR', 'E', 'Z', 'SR', 'P']);
export const VatDirectionSchema = z.enum(['FROM_PRICE_WITHOUT_VAT', 'FROM_PRICE_WITH_VAT']);

export const MinisaleResponseBodySchema = z.object({
  id: z.string(),
  customerBuyingStatement: z.boolean().nullable().optional(),
  createdBy: z.string().nullable().optional(),
  createdAt: z.string().nullable().optional(),
  vehicleData: InterestVehicleDataSchema.nullable().optional(),
  priceRequested: MoneySchema.nullable().optional(),
  priceOfferedWithoutVat: MoneySchema.nullable().optional(),
  priceOfferedWithVat: MoneySchema.nullable().optional(),
  priceOfferedBase: MoneySchema.nullable().optional(),
  expectedCosts: MoneySchema.nullable().optional(),
  reason: z.string().nullable().optional(),
  costReason: z.string().nullable().optional(),
  isVatDeductible: z.boolean().nullable().optional(),
  interestRate: z.number().nullable().optional(),
  vatType: VatTypeSchema.nullable().optional(),
  vatDirection: VatDirectionSchema.nullable().optional(),
  updatedBy: z.string().nullable().optional(),
  pricedBy: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
  pricedAt: z.string().nullable().optional(),
});

export const CreateMinisaleRequestBodySchema = z.object({
  interestId: z.string(),
  customerBuyingStatement: z.boolean(),
  interestRate: z.number().nullable().optional(),
  vehicleData: InterestVehicleDataSchema.nullable().optional(),
  vatType: VatTypeSchema.nullable().optional(),
  vatDirection: VatDirectionSchema.nullable().optional(),
  isVatDeductible: z.boolean(),
});

export const UpdateMinisaleRequestBodySchema = z.object({
  minisaleId: z.string(),
  interestId: z.string(),
  customerBuyingStatement: z.boolean(),
  interestRate: z.number().nullable().optional(),
  vehicleData: InterestVehicleDataSchema.nullable().optional(),
  vatType: VatTypeSchema.nullable().optional(),
  vatDirection: VatDirectionSchema.nullable().optional(),
  isVatDeductible: z.boolean(),
});

export const UpdateInterestUtmRequestBodySchema = UtmParametersSchema;

export const EndInterestRequestBodySchema = z.object({
  reasonCloseCodeId: z.string(),
  reasonCloseNote: z.string().nullable().optional(),
});

export const ChangeMinisaleCostReasonRequestBodySchema = z.object({
  costReason: z.string().nullable(),
});

export const ChangeMinisaleExpectedCostsRequestBodySchema = z.object({
  expectedCosts: MoneySchema.nullable(),
});

export const ChangeMinisalePriceOfferedRequestBodySchema = z.object({
  priceOffered: MoneySchema.nullable(),
});

export const ChangeMinisalePriceRequestedRequestBodySchema = z.object({
  priceRequested: MoneySchema.nullable(),
});

export const ChangeMinisaleReasonRequestBodySchema = z.object({
  reason: z.string().nullable(),
});

export const ChangeMinisaleVatDeductibleRequestBodySchema = z.object({
  isVatDeductible: z.boolean(),
});

// Define FullVehicleResponseBody schema to use the imported type
export const FullVehicleResponseBodySchema = z.custom<FullVehicleResponseBody>();

// Type inferences
export type CreateVehiclePriceReportRequestBody = z.infer<
  typeof CreateVehiclePriceReportRequestBodySchema
>;
export type CreatePriceReportResponseBody = z.infer<typeof CreatePriceReportResponseBodySchema>;
export type PriceReportResponseBody = z.infer<typeof PriceReportResponseBodySchema>;
export type DeleteInterestApiResponse = /** status 200  */ object;
export type DeleteInterestApiArg = {
  id: string;
};
export type VehicleInfo = z.infer<typeof VehicleInfoSchema>;
export type InterestActiveBusinessCaseResponseBody = z.infer<
  typeof InterestActiveBusinessCaseResponseBodySchema
>;
export type UtmParameters = z.infer<typeof UtmParametersSchema>;
export type InterestResponseBody = z.infer<typeof InterestResponseBodySchema>;
export type CreateInterestRequestBody = z.infer<typeof CreateInterestRequestBodySchema>;
export type CreateInterestApiResponse = /** status 201  */ InterestResponseBody;
export type CreateInterestApiArg = {
  createInterestRequestBody: CreateInterestRequestBody;
};
export type UpdateInterestRequestBody = z.infer<typeof UpdateInterestRequestBodySchema>;
export type UpdateInterestApiResponse = /** status 201  */ InterestResponseBody;
export type UpdateInterestApiArg = {
  id: string;
  updateInterestRequestBody: UpdateInterestRequestBody;
};
export type TranslationRowResponseBody = z.infer<typeof TranslationRowResponseBodySchema>;
export type InterestCatalogueResponseBody = z.infer<typeof InterestCatalogueResponseBodySchema>;
export const GetInterestV2ApiArgSchema = z.object({
  id: z.string(),
});

export const UserResponseBodyReadSchema = z.object({
  id: z.string(),
  email: z.string(),
  firstName: z.string(),
  lastName: z.string(),
});

export const VehicleInfoV2Schema = z.object({
  id: z.string().nullable(),
  make: z.string().nullable().optional(),
  trim: z.string().nullable().optional(),
  modelFamily: z.string().nullable().optional(),
  variant: z.string().nullable().optional(),
  title: z.string().nullable().optional(),
});

export const InterestActiveBusinessCaseResponseBodyV2Schema = z.object({
  businessCaseId: z.string(),
  code: z.string(),
  ownerId: z.string(),
  ownerFirstName: z.string(),
  ownerLastName: z.string(),
  businessCaseType: z.string().nullable(),
  businessCaseState: z.string(),
  vehicleInfo: VehicleInfoV2Schema,
  createdAt: z.string(),
  sourceCodeId: z.string().nullable(),
});

export const InterestResponseBodyV2Schema = z.object({
  id: z.string().nullable().optional(),
  customerId: z.string().nullable().optional(),
  createdBy: z.string().nullable().optional(),
  createdAt: z.string().nullable().optional(),
  sourceCodeId: z.string().nullable().optional(),
  type: z.string().nullable().optional(),
  state: z.string().nullable().optional(),
  reasonCloseCodeId: z.string().nullable().optional(),
  reasonCloseNote: z.string().nullable().optional(),
  minisalesIds: z.array(z.string()).optional(),
  minipurchasesIds: z.array(z.string()).optional(),
  closedBy: UserResponseBodySchema.nullable().optional(),
  closedAt: z.string().nullable().optional(),
  publicId: z.string().nullable().optional(),
  activeBusinessCases: z.array(InterestActiveBusinessCaseResponseBodyV2Schema).optional(),
  participationId: z.string().nullable().optional(),
  branchId: z.string().nullable().optional(),
  utm: UtmParametersSchema.nullable().optional(),
  updatedBy: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
  firstResponseAt: z.string().nullable().optional(),
  leadQuality: z.string().nullable().optional(),
  originLeadMessageId: z.string().nullable().optional(),
});

export const InterestResponseBodyV2ReadSchema = z.object({
  id: z.string().nullable().optional(),
  customerId: z.string().nullable().optional(),
  createdBy: z.string().nullable().optional(),
  createdAt: z.string().nullable().optional(),
  sourceCodeId: z.string().nullable().optional(),
  type: z.string().nullable().optional(),
  state: z.string().nullable().optional(),
  reasonCloseCodeId: z.string().nullable().optional(),
  reasonCloseNote: z.string().nullable().optional(),
  minisalesIds: z.array(z.string()).optional(),
  minipurchasesIds: z.array(z.string()).optional(),
  closedBy: UserResponseBodyReadSchema.nullable().optional(),
  closedAt: z.string().nullable().optional(),
  publicId: z.string().nullable().optional(),
  activeBusinessCases: z.array(InterestActiveBusinessCaseResponseBodyV2Schema).optional(),
  participationId: z.string().nullable().optional(),
  branchId: z.string().nullable().optional(),
  utm: UtmParametersSchema.nullable().optional(),
  updatedBy: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
  firstResponseAt: z.string().nullable().optional(),
  leadQuality: z.string().nullable().optional(),
  originLeadMessageId: z.string().nullable().optional(),
});

export type GetInterestV2ApiResponse = /** status 200  */ InterestResponseBodyV2Read;
export type GetInterestV2ApiArg = z.infer<typeof GetInterestV2ApiArgSchema>;
export type UserResponseBodyRead = z.infer<typeof UserResponseBodyReadSchema>;
export type VehicleInfoV2 = z.infer<typeof VehicleInfoV2Schema>;
export type InterestActiveBusinessCaseResponseBodyV2 = z.infer<
  typeof InterestActiveBusinessCaseResponseBodyV2Schema
>;
export type InterestResponseBodyV2 = z.infer<typeof InterestResponseBodyV2Schema>;
export type InterestResponseBodyV2Read = z.infer<typeof InterestResponseBodyV2ReadSchema>;
export type GetInterestCatalogueApiResponse = /** status 201  */ InterestCatalogueResponseBody;
export type GetInterestApiResponse = /** status 200  */ InterestResponseBody;
export type GetInterestApiArg = {
  id: string;
};
export type ConvertForAllVehiclesApiResponse = /** status 201  */ InterestResponseBody;
export type ConvertForAllVehiclesApiArg = {
  interestId: string;
  validateMandatoryFields?: boolean | null;
};
export type ConvertForBuyingVehicleApiResponse = /** status 201  */ InterestResponseBody;
export type ConvertForBuyingVehicleApiArg = {
  interestId: string;
  validateMandatoryFields?: boolean | null;
};
export type ConvertForSellingVehicleApiResponse = /** status 201  */ InterestResponseBody;
export type ConvertForSellingVehicleApiArg = {
  interestId: string;
  vehicleId: string;
};
export type InterestStatisticsResponseBody = z.infer<typeof InterestStatisticsResponseBodySchema>;
export type GetInterestStatisticsApiResponse = /** status 200  */ InterestStatisticsResponseBody;
export type GetInterestStatisticsApiArg = {
  branchId?: string | null;
};
export type CreateInterestPriceReportApiResponse = /** status 202  */ CreatePriceReportResponseBody;
export type CreateInterestPriceReportApiArg = {
  interestId: string;
  body: CreateVehiclePriceReportRequestBody;
};
export type GetInterestPriceReportsApiResponse = /** status 200  */ PriceReportResponseBody[];
export type GetInterestPriceReportsApiArg = {
  interestId: string;
};
export type CreateMinipurchaseApiResponse = /** status 201  */ MinipurchaseResponseBody;
export type CreateMinipurchaseApiArg = {
  body: CreateMinipurchaseRequestBody;
};
export type UpdateMinipurchaseApiResponse = /** status 201  */ MinipurchaseResponseBody;
export type UpdateMinipurchaseApiArg = {
  interestId: string;
  body: UpdateMinipurchaseRequestBody;
};
export type GetMinipurchaseApiResponse = /** status 201  */ MinipurchaseResponseBody;
export type GetMinipurchaseApiArg = {
  minipurchaseId: string;
};
export type CreateMinisaleApiResponse = /** status 201  */ MinisaleResponseBody;
export type CreateMinisaleApiArg = {
  createMinisaleRequestBody: CreateMinisaleRequestBody;
  validateMandatoryFields?: boolean;
};
export type UpdateMinisaleApiResponse = /** status 201  */ MinisaleResponseBody;
export type UpdateMinisaleApiArg = {
  updateMinisaleRequestBody: UpdateMinisaleRequestBody;
  validateMandatoryFields?: boolean;
};
export type DeleteMinisaleApiResponse = /** status 200  */ object;
export type DeleteMinisaleApiArg = {
  minisaleId: string;
  interestId: string;
};
export type GetMinisaleApiResponse = /** status 201  */ MinisaleResponseBody;
export type GetMinisaleApiArg = {
  minisaleId: string;
};
export type GetOfferedVehiclesListApiResponse = /** status 201  */ FullVehicleResponseBody[];
export type GetOfferedVehiclesListApiArg = {
  body: GetOfferedVehiclesListRequestBody;
};
export type GetOfferedVehiclesListRequestBody = z.infer<
  typeof GetOfferedVehiclesListRequestBodySchema
>;

export type ChangeInterestBranchRequestBody = z.infer<typeof ChangeInterestBranchRequestBodySchema>;
export type ChangeInterestCustomerRequestBody = z.infer<
  typeof ChangeInterestCustomerRequestBodySchema
>;
export type ChangeInterestBranchApiArg = z.infer<typeof ChangeInterestBranchApiArgSchema>;
export type ChangeInterestCustomerApiArg = z.infer<typeof ChangeInterestCustomerApiArgSchema>;

export type ChangeInterestBranchApiResponse = unknown;
export type ChangeInterestCustomerApiResponse = void;
export type MinipurchaseFilteredSpecifications = z.infer<
  typeof MinipurchaseFilteredSpecificationsSchema
>;
export type MinipurchaseResponseBody = z.infer<typeof MinipurchaseResponseBodySchema>;
export type CreateMinipurchaseRequestBody = z.infer<typeof CreateMinipurchaseRequestBodySchema>;
export type UpdateMinipurchaseRequestBody = z.infer<typeof UpdateMinipurchaseRequestBodySchema>;
export type MinisaleResponseBody = z.infer<typeof MinisaleResponseBodySchema>;
export type CreateMinisaleRequestBody = z.infer<typeof CreateMinisaleRequestBodySchema>;
export type UpdateMinisaleRequestBody = z.infer<typeof UpdateMinisaleRequestBodySchema>;
export type InterestVehicleType = z.infer<typeof InterestVehicleTypeSchema>;
export type InterestVehicleData = z.infer<typeof InterestVehicleDataSchema>;

export type UpdateInterestUtmApiResponse = unknown;
export type UpdateInterestUtmRequestBody = z.infer<typeof UpdateInterestUtmRequestBodySchema>;
export type UpdateInterestUtmApiArg = {
  id: string;
  updateInterestUtmRequestBody: UpdateInterestUtmRequestBody;
};

export type EndInterestApiResponse = unknown;
export type EndInterestApiArg = {
  id: string;
  endInterestRequestBody: EndInterestRequestBody;
};
export type OpenInterestApiResponse = unknown;
export type OpenInterestApiArg = {
  id: string;
};
export type ChangeMinisaleCostReasonApiResponse = /** status 204  */ void;
export type ChangeMinisaleCostReasonApiArg = {
  interestId: string;
  minisaleId: string;
  changeMinisaleCostReasonRequestBody: ChangeMinisaleCostReasonRequestBody;
};
export type ChangeMinisaleExpectedCostsApiResponse = /** status 204  */ void;
export type ChangeMinisaleExpectedCostsApiArg = {
  interestId: string;
  minisaleId: string;
  changeMinisaleExpectedCostsRequestBody: ChangeMinisaleExpectedCostsRequestBody;
};
export type ChangeMinisalePriceOfferedApiResponse = /** status 204  */ void;
export type ChangeMinisalePriceOfferedApiArg = {
  interestId: string;
  minisaleId: string;
  changeMinisalePriceOfferedRequestBody: ChangeMinisalePriceOfferedRequestBody;
};
export type ChangeMinisalePriceRequestedApiResponse = /** status 204  */ void;
export type ChangeMinisalePriceRequestedApiArg = {
  interestId: string;
  minisaleId: string;
  changeMinisalePriceRequestedRequestBody: ChangeMinisalePriceRequestedRequestBody;
};
export type ChangeMinisaleReasonApiResponse = /** status 204  */ void;
export type ChangeMinisaleReasonApiArg = {
  interestId: string;
  minisaleId: string;
  changeMinisaleReasonRequestBody: ChangeMinisaleReasonRequestBody;
};
export type ChangeMinisaleVatDeductibleApiResponse = /** status 204  */ void;
export type ChangeMinisaleVatDeductibleApiArg = {
  interestId: string;
  minisaleId: string;
  changeMinisaleVatDeductibleRequestBody: ChangeMinisaleVatDeductibleRequestBody;
};

export type RemoveMinipurchaseOfferedVehicleApiResponse = /** status 204  */ void;
export type RemoveMinipurchaseOfferedVehicleApiArg = {
  minipurchaseId: string;
  vehicleId: string;
};

export type EndInterestRequestBody = z.infer<typeof EndInterestRequestBodySchema>;
export type ChangeMinisaleCostReasonRequestBody = z.infer<
  typeof ChangeMinisaleCostReasonRequestBodySchema
>;
export type ChangeMinisaleExpectedCostsRequestBody = z.infer<
  typeof ChangeMinisaleExpectedCostsRequestBodySchema
>;
export type ChangeMinisalePriceOfferedRequestBody = z.infer<
  typeof ChangeMinisalePriceOfferedRequestBodySchema
>;
export type ChangeMinisalePriceRequestedRequestBody = z.infer<
  typeof ChangeMinisalePriceRequestedRequestBodySchema
>;
export type ChangeMinisaleReasonRequestBody = z.infer<typeof ChangeMinisaleReasonRequestBodySchema>;
export type ChangeMinisaleVatDeductibleRequestBody = z.infer<
  typeof ChangeMinisaleVatDeductibleRequestBodySchema
>;

export const AddMinipurchaseOfferedVehicleRequestBodySchema = z.object({
  vehicleId: z.string(),
});

export const AddMinipurchaseOfferedVehicleApiArgSchema = z.object({
  minipurchaseId: z.string(),
  addMinipurchaseOfferedVehicleRequestBody: AddMinipurchaseOfferedVehicleRequestBodySchema,
});

export type AddMinipurchaseOfferedVehicleRequestBody = z.infer<
  typeof AddMinipurchaseOfferedVehicleRequestBodySchema
>;
export type AddMinipurchaseOfferedVehicleApiResponse = /** status 204  */ void;
export type AddMinipurchaseOfferedVehicleApiArg = z.infer<
  typeof AddMinipurchaseOfferedVehicleApiArgSchema
>;

export const InterestTestDriveRequestBodySchema = z.object({
  vehicleId: z.string().nullable(),
  note: z.string().nullable(),
  scheduledAt: z.string(),
});

export const InterestTestDriveResponseBodySchema = z.object({
  interestId: z.string(),
  testDriveId: z.string(),
  vehicleId: z.string().nullable(),
  note: z.string().nullable(),
  scheduledAt: z.string(),
  createdBy: z.string(),
  createdAt: z.string(),
  updatedBy: z.string().nullable(),
  updatedAt: z.string().nullable(),
});

export const AddInterestTestDriveApiArgSchema = z.object({
  interestId: z.string(),
  interestTestDriveRequestBody: InterestTestDriveRequestBodySchema,
});

export const GetInterestTestDriveApiArgSchema = z.object({
  interestId: z.string(),
  interestTestDriveId: z.string(),
});

export const RemoveInterestTestDriveApiArgSchema = z.object({
  interestId: z.string(),
  interestTestDriveId: z.string(),
});

export const UpdateInterestTestDriveApiArgSchema = z.object({
  interestId: z.string(),
  interestTestDriveId: z.string(),
  interestTestDriveRequestBody: InterestTestDriveRequestBodySchema,
});

export const PriceReportPreviewResponseBodySchema = z.object({
  stock: z.number().nullable(),
  sold: z.number().nullable(),
  recommendedSellingPrice: MoneySchema.nullable(),
});

export const PriceReportPreviewApiArgSchema = z.object({
  interestId: z.string(),
  createVehiclePriceReportRequestBody: CreateVehiclePriceReportRequestBodySchema,
});

// Type inferences
export type AddInterestTestDriveApiResponse = /** status 201  */ UuidResponseBody;
export type AddInterestTestDriveApiArg = z.infer<typeof AddInterestTestDriveApiArgSchema>;
export type GetInterestTestDriveApiResponse = /** status 200  */ InterestTestDriveResponseBody;
export type GetInterestTestDriveApiArg = z.infer<typeof GetInterestTestDriveApiArgSchema>;
export type RemoveInterestTestDriveApiResponse = unknown;
export type RemoveInterestTestDriveApiArg = z.infer<typeof RemoveInterestTestDriveApiArgSchema>;
export type UpdateInterestTestDriveApiResponse = unknown;
export type UpdateInterestTestDriveApiArg = z.infer<typeof UpdateInterestTestDriveApiArgSchema>;

export type InterestTestDriveRequestBody = z.infer<typeof InterestTestDriveRequestBodySchema>;
export type InterestTestDriveResponseBody = z.infer<typeof InterestTestDriveResponseBodySchema>;

export type PriceReportPreviewResponseBody = z.infer<typeof PriceReportPreviewResponseBodySchema>;
export type PriceReportPreviewApiResponse = /** status 200  */ PriceReportPreviewResponseBody;
export type PriceReportPreviewApiArg = z.infer<typeof PriceReportPreviewApiArgSchema>;
